import { merge } from 'lodash';
import { useState } from 'react';

import pushToQueue from '@/shared/api/fulfilment/orders/pushToQueue';
import cancelQueues from '@/shared/api/fulfilment/queue/cancelQueues';
import packQueue from '@/shared/api/fulfilment/queue/packQueue';
import pauseQueues from '@/shared/api/fulfilment/queue/pauseQueues';
import pickQueue from '@/shared/api/fulfilment/queue/pickQueue';
import resumeQueues from '@/shared/api/fulfilment/queue/resumeQueues';
import scheduleQueues from '@/shared/api/fulfilment/queue/scheduleQueues';
import submitQueues from '@/shared/api/fulfilment/queue/submitQueues';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import Queue from '@/shared/types/fulfilment/queue.type';
import Response from '@/shared/types/response';

import useFulfilmentQueueTable_v2 from './useFulfilmentQueueTable_v2';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  setTable: ReturnType<typeof useFulfilmentQueueTable_v2>['setTable'];
  setRefreshTableIndex: ReturnType<
    typeof useFulfilmentQueueTable_v2
  >['setRefreshTableIndex'];
};

const useTableActions = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  setTable,
  setRefreshTableIndex,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const [actionErrors, setActionErrors] = useState<Record<string, string>>({
    fulfil_qty: '',
  });

  const onSetActionErrors = (responses: Response<Partial<Queue>[]>[]) => {
    responses
      .filter((item) => item.success === false)
      .forEach((response) => {
        setActionErrors((prev) => ({
          ...prev,
          [response.data[0].fulfilment_queue_id || '']: response.msg,
        }));
      });
  };

  const handleFulfilmentPick = async (
    values: Queue[],
    reason?: 'update-table',
  ) => {
    if (reason === 'update-table') {
      setTable((prev) => ({ ...prev, data: merge(prev.data, values) }));
    } else {
      const requestBody = values.map((item) => ({
        fulfil_qty: item.fulfil_qty,
        fulfilment_queue_id: item.fulfilment_queue_id,
      }));

      const responses = await pickQueue({
        body: requestBody,
        onUnauthorized: redirectLogin,
      });

      if (responses.every((response) => response.success)) {
        setRefreshTableIndex((prev) => prev + 1);
      } else {
        onSetActionErrors(responses);
      }
    }
  };

  const handleFulfilmentPack = async (values: Queue[]) => {
    const requestBody = values.map((item) => ({
      fulfil_qty: item.fulfil_qty,
      fulfilment_queue_id: item.fulfilment_queue_id,
    }));

    const responses = await packQueue({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (responses.every((response) => response.success)) {
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onSetActionErrors(responses);
    }
  };

  const handleFulfilQtyUpdate = async (
    value: Queue,
    reason?: 'update-table',
  ) => {
    switch (value.status) {
      case 'QUEUED':
      case 'PICKING':
      case 'PARTIAL':
      case 'HELD':
        return await handleFulfilmentPick([value], reason);
      case 'PACKING':
        return await handleFulfilmentPack([value]);
      default:
        return false;
    }
  };

  const handlePauseQueues = async (values: Queue[]) => {
    const requestBody = values.map((item) => ({
      fulfilment_queue_id: item.fulfilment_queue_id,
    }));

    const responses = await pauseQueues({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (responses.every((response) => response.success)) {
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onSetActionErrors(responses);
    }
  };

  const handleResumeQueues = async (values: Queue[]) => {
    const requestBody = values.map((item) => ({
      fulfilment_queue_id: item.fulfilment_queue_id,
      fulfil_qty: item.fulfil_qty,
    }));

    const responses = await resumeQueues({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (responses.every((response) => response.success)) {
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onSetActionErrors(responses);
    }
  };

  const handlePauseResume = async (values: Queue[]) => {
    const { status } = values[0];

    switch (status) {
      case 'PAUSED':
      case 'HELD':
        await handleResumeQueues(values);
        break;
      default:
        await handlePauseQueues(values);
        break;
    }
  };

  const handleScheduleQueues = async (values: Queue[]) => {
    const requestBody = values.map((item) => ({
      ts_submit: item.ts_submit,
      fulfilment_queue_id: item.fulfilment_queue_id,
    }));

    const responses = await scheduleQueues({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (responses.every((response) => response.success)) {
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onSetActionErrors(responses);
    }
  };

  const handleSubmitQueues = async (requestBody: Record<string, unknown>) => {
    onUpdateDialogState(DialogName.SubmitToFulfilment, {
      loading: true,
    });

    const response = await submitQueues({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateDialogState(DialogName.SubmitToFulfilment, {
        open: false,
        loading: false,
      });
      onUpdateNotificationBar({
        open: true,
        message: 'Fulfilment queue was successfully submitted',
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.SubmitToFulfilment, {
        errors: [response.msg],
        loading: false,
      });
    }
  };

  const handleCancelQueues = async (values: Queue[]) => {
    const responses = await cancelQueues({
      body: values,
      onUnauthorized: redirectLogin,
    });

    if (responses.every((response) => response.success)) {
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.CancelQueues, {
        errors: responses
          .filter((response) => !response.success)
          .map((item) => item.msg),
        loading: false,
      });
    }
  };

  const handlePushToQueue = async (orderId: string) => {
    const response = await pushToQueue({
      orderId,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.PushToQueue, {
        open: true,
        errors: [response.msg],
      });
    }
  };

  return {
    actionErrors,
    handleFulfilmentPack,
    handleFulfilmentPick,
    handleFulfilQtyUpdate,
    handlePauseResume,
    handleScheduleQueues,
    handleSubmitQueues,
    handleCancelQueues,
    handlePushToQueue,
  };
};

export default useTableActions;
